import { useGetRehabForms, useGetTransitionalCare } from "apollo/hooks/queries";
import { isLoading } from "apollo/utils";
import { DOCS_FORM_NAMES } from "core/consts";
import { getPatientName } from "core/model/patients";
import {
  Auction,
  AuctionForm,
  DocsFormName,
  DocsFormSourceDocument,
  DocsFormStatusResponse,
  Patient,
  RehabForms,
  TransitionalCare,
  ValueOf,
} from "core/types";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "ds/ui";
import {
  IMPORT_AUCTION_ID,
  REIMPORT_AUCTION_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import { RefreshCwIcon, SparklesIcon, XIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslations } from "translations";
import { useRecareDocsContext } from "../context";
import { FormFooter } from "./Footer";
import { DocsFormFields } from "./FormFields";
import { DocsFormDataMapped } from "./Forms";
import { useGetDischargeLetterForms } from "./Forms/DischargeLetters/useGetForm";
import { HEADER_BANNERS, HeaderBannerAction } from "./HeaderBanners";
import { DocsModalSources } from "./Sources";
import { useDocsForm } from "./useDocsForm";

export const DOCS_FORM_FOOTER_SCREENS = {
  DEFAULT: "default",
  DELETE_CONFIRMATION: "delete-confirmation",
  SAVE_CONFIRMATION: "save-confirmation",
  WARN_OPEN_PDF: "warn-open-pdf",
  WARN_SEND_TO_HIS: "warn-send-to-his",
} as const;

export type DocsFormFooterScreen = ValueOf<typeof DOCS_FORM_FOOTER_SCREENS>;

const DocsModalHeader = ({
  hasPreviousFormValue,
  header,
  headerBannerAction,
  importFormData,
  isImportingForm,
  noDocumentsExtracted,
  onClose,
  patient,
}: {
  headerBannerAction: HeaderBannerAction;
  importFormData: () => Promise<void>;
  isImportingForm: boolean;
  hasPreviousFormValue: boolean;
  noDocumentsExtracted: boolean;
  patient: Patient;
  header: string;
  onClose: () => void;
}) => {
  const translations = useTranslations();

  return (
    <ModalHeader className="flex w-full items-center justify-between">
      <div className="flex flex-col gap-1">
        <p className="text-body-md font-semibold text-gray-600">{`${getPatientName(
          patient,
        )} (${patient.user_id})`}</p>
        <h1 className="text-heading-lg font-bold">{header}</h1>
      </div>
      <div className="flex gap-4">
        <Button
          color="primary"
          id={hasPreviousFormValue ? REIMPORT_AUCTION_ID : IMPORT_AUCTION_ID}
          isDisabled={
            headerBannerAction.banner === HEADER_BANNERS.EXTRACTING ||
            noDocumentsExtracted
          }
          isLoading={isImportingForm}
          size="sm"
          startContent={
            hasPreviousFormValue ? <RefreshCwIcon /> : <SparklesIcon />
          }
          variant={hasPreviousFormValue ? "ghost" : "solid"}
          onPress={() => {
            importFormData();
          }}
        >
          {hasPreviousFormValue
            ? translations.extractDocs.splitView.header.buttonRefreshData
            : translations.extractDocs.splitView.header.buttonImportData}
        </Button>
        <Button
          color="primary"
          endContent={<XIcon />}
          id="close-modal"
          size="sm"
          variant="light"
          onPress={onClose}
        >
          {translations.extractDocs.splitView.header.buttonClose}
        </Button>
      </div>
    </ModalHeader>
  );
};

export type SourceSelected<T extends DocsFormName> = {
  fieldName: keyof DocsFormDataMapped<T>;
  sourceDocuments: DocsFormSourceDocument[];
};

const useSelectedSource = <T extends DocsFormName>(
  docsFillFormResult: DocsFormStatusResponse | null,
) => {
  const [selectedSource, setSelectedSource] = useState<
    SourceSelected<T> | undefined
  >(undefined);

  const handleSetSelectedSource = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      setSelectedSource({
        fieldName,
        sourceDocuments:
          docsFillFormResult?.form?.[String(fieldName)]?.source_documents ?? [],
      });
    },
    [docsFillFormResult?.form],
  );

  const isSourceSelected = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return selectedSource?.fieldName === fieldName;
    },
    [selectedSource],
  );

  const getSourceDocuments = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return (
        docsFillFormResult?.form?.[String(fieldName)]?.source_documents ?? []
      );
    },
    [docsFillFormResult],
  );

  return {
    getSourceDocuments,
    handleSetSelectedSource,
    isSourceSelected,
    selectedSource,
  };
};

export type SourceInformation<T extends DocsFormName> = ReturnType<
  typeof useSelectedSource<T>
>;

const DocsModalContent = ({
  auction,
  dischargeLetterForms,
  formName,
  patient,
  rehabForms,
  transitionalCare,
}: {
  formName: DocsFormName;
  auction: Auction;
  patient: Patient;
  rehabForms: RehabForms | undefined;
  transitionalCare: TransitionalCare | undefined;
  dischargeLetterForms: AuctionForm[];
}) => {
  const translations = useTranslations();

  const {
    config,
    confirmationChecked,
    docsFillFormResult,
    footerBanner,
    footerScreen,
    form,
    handleBack,
    handleDelete,
    handleDeleteProgress,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasPreviousFormValue,
    headerBannerAction,
    importFormData,
    isImportingForm,
    methods,
    noDocumentsExtracted,
    onCloseHeaderBanner,
    onSubmitProgress,
    progressOpenPdf,
    progressSendToHis,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    setHeaderBannerAction,
    skipRequiredValidation,
  } = useDocsForm({
    auction,
    dischargeLetterForms,
    formName,
    patient,
    rehabForms,
    transitionalCare,
  });
  const { setFormModal } = useRecareDocsContext();

  const sourceInformation = useSelectedSource(docsFillFormResult);

  return (
    <FormProvider {...methods}>
      <Form className="gap-0" onSubmit={handleSubmit}>
        <DocsModalHeader
          hasPreviousFormValue={hasPreviousFormValue}
          header={config.header({ translations })}
          headerBannerAction={headerBannerAction}
          importFormData={importFormData}
          isImportingForm={isImportingForm}
          noDocumentsExtracted={noDocumentsExtracted}
          patient={patient}
          onClose={() => {
            if (methods.formState.isDirty) {
              setHeaderBannerAction({
                banner: HEADER_BANNERS.FORM_DIRTY_WARNING,
              });
            } else {
              setFormModal(null);
            }
          }}
        />
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex w-full flex-row gap-0 overflow-hidden p-0">
          <div
            // TODO: not ideal, fix
            className="max-h-[calc(95vh-80px)] w-[50%] overflow-y-auto"
          >
            <DocsFormFields<typeof formName>
              auction={auction}
              config={config}
              formName={formName}
              headerBannerAction={headerBannerAction}
              importData={importFormData}
              skipRequiredValidation={skipRequiredValidation}
              sourceInformation={sourceInformation}
              onCloseHeaderBanner={onCloseHeaderBanner}
            />
            <FormFooter
              canPrint={config.config.canPrint}
              canSendToHis={config.config.canSendToHis}
              confirmationChecked={confirmationChecked}
              footerBanner={footerBanner}
              footerScreen={footerScreen}
              handleOpenPdf={handleOpenPdf}
              handleSendToHis={handleSendToHis}
              hasPreviousFormValue={hasPreviousFormValue}
              progressOpenPdf={progressOpenPdf}
              progressSendToHis={progressSendToHis}
              setConfirmationChecked={setConfirmationChecked}
              setFooterBanner={setFooterBanner}
              setFooterScreen={setFooterScreen}
              updatedAt={
                formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE
                  ? transitionalCare?.updated_at
                  : form?.updated_at
              }
              onBack={handleBack}
              onDelete={handleDelete}
              onDeleteProgress={handleDeleteProgress}
              onSubmitProgress={onSubmitProgress}
            />
          </div>
          <DocsModalSources
            auction={auction}
            patient={patient}
            sourceDocuments={
              sourceInformation.selectedSource?.sourceDocuments ?? []
            }
          />
        </ModalBody>
      </Form>
    </FormProvider>
  );
};

export const RecareDocsModal = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const { formModal } = useRecareDocsContext();

  const [rehabFormsQueryProgress, rehabForms] = useGetRehabForms({
    auctionId: auction.id,
    options: { skip: formModal === DOCS_FORM_NAMES.TRANSITIONAL_CARE },
    patientId: patient.id,
  });

  const [transitionalCareQueryProgress, transitionalCare] =
    useGetTransitionalCare({
      auctionId: auction.id,
      options: {
        skip: formModal !== DOCS_FORM_NAMES.TRANSITIONAL_CARE,
      },
      patientId: patient.id,
    });

  const {
    forms: dischargeLetterForms,
    queryProgress: dischargeLetterQueryProgress,
  } = useGetDischargeLetterForms({
    auctionId: auction.id,
  });

  return (
    <Modal hideCloseButton isOpen={!!formModal}>
      <ModalContent className="flex min-h-[95vh] w-full min-w-[95vw] flex-col">
        {formModal &&
          (isLoading([
            rehabFormsQueryProgress,
            dischargeLetterQueryProgress,
            transitionalCareQueryProgress,
          ]) ? (
            <ModalBody className="flex h-full w-full items-center justify-center">
              <Spinner
                color="primary"
                size="lg"
                testId="spinner-loading-docs"
              />
            </ModalBody>
          ) : (
            <DocsModalContent
              auction={auction}
              dischargeLetterForms={dischargeLetterForms}
              formName={formModal}
              patient={patient}
              rehabForms={rehabForms}
              transitionalCare={transitionalCare}
            />
          ))}
      </ModalContent>
    </Modal>
  );
};
